import { useEffect } from "react";
import { Icon } from "../components/Icon";
import { signOut, type SignedInUser } from "../lib/auth";
import { BRAND, logoWidth } from "../lib/config";
import { hashParams, useHash } from "../lib/route";
import type { Profile } from "../lib/supabase";
import { MyShifts, PackView } from "./CrewPack";
import { HelpHost, HelpScreen, WhatsNewCard } from "../help/Help";
import { crewPayInUse, MyPay } from "../modules/pay/CrewPay";

/**
 * The portal for crew (controllers), built for phones: their shifts and the job pack for each.
 * Routes: #/ (my shifts), #/shift/<booking id> and #/help (guides for crew, and reporting a problem).
 */
export function CrewApp({ user, profile }: { user: SignedInUser; profile: Profile }) {
  const hash = useHash();
  const [section] = hash.replace(/^#\/?/, "").split("/");
  const [id] = hashParams(hash);

  useEffect(() => {
    window.scrollTo(0, 0);
    requestAnimationFrame(() => document.getElementById("page-title")?.focus({ preventScroll: true }));
  }, [hash]);

  return (
    <div className="crewapp">
      <header className="crewbar">
        <a href="#/"><img src={BRAND.logoSmall} alt={`${BRAND.name}, your shifts`} width={logoWidth(36)} height="36" /></a>
        <span className="crewbar__name">{profile.full_name || user.email}</span>
        {crewPayInUse() && <a className="icon-btn icon-btn--dark" href="#/pay" aria-label="Pay"><Icon name="dollar" size={22} /></a>}
        <a className="icon-btn icon-btn--dark" href="#/help" aria-label="Help"><Icon name="help" size={22} /></a>
        <button type="button" className="icon-btn icon-btn--dark" aria-label="Sign out" onClick={() => void signOut()}>
          <Icon name="logout" size={22} />
        </button>
      </header>
      <main id="main" tabIndex={-1} className="crewmain">
        {section === "shift" && id ? (
          <PackView key={id} bookingId={id} backHref="#/" backLabel="Your shifts" />
        ) : section === "pay" && crewPayInUse() ? (
          <MyPay />
        ) : section === "help" ? (
          <HelpScreen />
        ) : (
          <>
            <WhatsNewCard />
            <MyShifts />
          </>
        )}
      </main>
      <HelpHost />
    </div>
  );
}
